import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const NotificationPanel = ({ notifications }) => {
  const [filter, setFilter] = useState('all');

  const getNotificationIcon = (type) => {
    const iconMap = {
      success: 'CheckCircle',
      warning: 'AlertTriangle',
      error: 'XCircle',
      info: 'Info',
      message: 'MessageCircle',
      system: 'Settings'
    };
    return iconMap?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      success: 'text-success',
      warning: 'text-warning',
      error: 'text-error',
      info: 'text-primary',
      message: 'text-accent',
      system: 'text-muted-foreground'
    };
    return colorMap?.[type] || 'text-muted-foreground';
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications?.filter(n => n?.type === filter);

  const filterOptions = [
    { value: 'all', label: 'All', count: notifications?.length },
    { value: 'success', label: 'Success', count: notifications?.filter(n => n?.type === 'success')?.length },
    { value: 'warning', label: 'Warnings', count: notifications?.filter(n => n?.type === 'warning')?.length },
    { value: 'error', label: 'Errors', count: notifications?.filter(n => n?.type === 'error')?.length }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
        <Button variant="ghost" size="sm">
          <Icon name="Settings" size={16} className="mr-2" />
          Settings
        </Button>
      </div>
      <div className="flex items-center space-x-2 mb-4 overflow-x-auto">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
              filter === option?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <span>{option?.label}</span>
            {option?.count > 0 && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                filter === option?.value
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-background text-foreground'
              }`}>
                {option?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {filteredNotifications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} className="text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-muted-foreground">No notifications found</p>
          </div>
        ) : (
          filteredNotifications?.map((notification, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-200">
              <div className={`p-2 rounded-full bg-muted ${getNotificationColor(notification?.type)}`}>
                <Icon name={getNotificationIcon(notification?.type)} size={16} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {notification?.title}
                  </h4>
                  <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                    {notification?.time}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-2">
                  {notification?.message}
                </p>
                
                {notification?.actions && (
                  <div className="flex items-center space-x-2">
                    {notification?.actions?.map((action, actionIndex) => (
                      <Button
                        key={actionIndex}
                        variant={action?.variant || 'outline'}
                        size="xs"
                        onClick={action?.onClick}
                      >
                        {action?.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              
              {!notification?.read && (
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;